<template>
  <article class="container-fluid">
    <PageHeader
      :page="page"
      has-return
      left-side-class="col-xl-6"
      right-side-class="col-xl-6 d-flex align-items-end justify-content-end pb-2"
    >
      <template v-slot:customActions>
        <ActionButton
          v-if="hasAddTeacherManuallyPermission && !showAddTeacherSection"
          class="m-1"
          text="Añadir Maestro"
          variant="outline-success"
          i="fas fa-user-plus"
          @click="showAddTeacherSection = true"
        />
        <ActionButton
          class="m-1"
          text="Guardar"
          variant="success"
          i="fas fa-save"
          @click="onSave"
        />
        <DropDown
          v-if="visibleActionItems.length"
          :items="visibleActionItems"
          title="Acciones"
          class="m-1 actions-dropdown"
          always-show-dropdown
        />
      </template>
    </PageHeader>
    <AddTeacherSection
      v-if="showAddTeacherSection"
      title="Añadir Maestro"
      :school-alternative-location-id="getId"
      class="mt-1"
      @on-close="showAddTeacherSection = false"
      @on-save="onTeacherAdded"
    />
    <InformationHeader :value="model" />
    <div v-if="showChangeStudentCapacity" class="bg-gray-9 p-10px my-3 rounded">
      <EditStudentCapacity
        :location-id="getId"
        :capacity="model.studentCapacity"
        :limit="model.studentCapacityLimit"
        @success="onChangeStudentCapacity"
        @cancel="showChangeStudentCapacity = false"
      />
    </div>
    <ValidationObserver ref="general-information-form">
      <GeneralInformation
        :value="model"
        :is-school-in-alliance="isSchoolInAlliance"
        :teacher-selection-disabled="showChangeTeacherButton"
        :collections="collections"
      />
    </ValidationObserver>
    <AssistantsSection
      v-if="model.id && isBulkAssistantAssignmentAvailable"
      ref="assistants-section"
      v-model="model.assistants"
      :alternative-location-teacher-id="model.id"
      :academic-year-id="model.academicYear.id"
      :school-id="model.school.id"
    />
    <template v-if="model.id">
      <SectionTitle
        title="Información de Estudiantes"
        class="my-4"
        :has-line="true"
      />
      <CustomSeparator
        id="custom-separator-line"
        :options="separatorOptions"
        class="my-3 custom-separator-line"
        @selected="onStudentFilterChanged"
      />
      <StudentsHeaderFilter
        :key="currentStudentFilterType"
        :show-add-student-button="showAddStudentButton"
        :show-sync-student-button="showSyncStudentButton"
        :student-count="pagination.totalItemCount"
        :text-count="textCount"
        @show-add-student="showAddStudent = $event"
        @on-search="onSearch"
        @on-sync="onSync"
      >
        <AddStudent
          v-if="showAddStudentSection"
          :collections="collections"
          :teacher-id="currentTeacherId"
          :school-alternative-location-id="getId"
          @on-added="() => doSearch()"
          @cancel="showAddStudent = $event"
        />
      </StudentsHeaderFilter>
      <CustomResultNotFound
        v-if="model.id && !students.length"
        class="mt-4 row pt-3"
        text="No hay información disponible."
      />
      <div v-else>
        <StudentResultRow
          v-for="student in students"
          :key="student.studentSieId"
          :value="student"
          :collections="collections"
          :teacher-id="currentTeacherId"
          :show-actions="true"
          :show-remove-action="isAssignedStudentsTabActive"
          :show-assign-action="isAvailableStudentsTabActive"
          :current-page="pagination.currentPage"
          :school-alternative-location-id="getId"
          @on-removed="() => onStudentRemoved()"
          @on-assigned="() => onAddRemoveHandler()"
        />
      </div>

      <Pagination
        v-if="pagination.totalItemCount > pagination.perPage"
        :data="pagination"
        :page-size="pagination.perPage"
        parent-id="custom-separator-line"
        @pagination-go-page="doSearch"
      />
    </template>
  </article>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import DropDown from 'dropdown';
import CustomSeparator from 'custom-separator';
import Pagination from 'pagination';
import CustomResultNotFound from 'custom-result-not-found';
import SectionTitle from 'SectionTitle';
import InformationHeader from './components/InformationHeader';
import GeneralInformation from './components/GeneralInformation';
import StudentResultRow from './components/StudentResultRow';
import StudentsHeaderFilter from './components/StudentsHeaderFilter';
import AddStudent from './components/AddStudent';
import EditStudentCapacity from './components/EditStudentCapacity';
import AssistantsSection from './components/AssistantsSection';
import AddTeacherSection from './components/AddTeacherSection.vue';
import alert from '@/mixin/alert.mixin';
import resourcesService from '@/services/resources';
import {
  saveAlternativeLocationTeacher,
  assignAssistantsToSAEE10,
  syncAssignedStudent,
  getAlternativeLocationTeacher,
  getAlternativeLocationTeacherStudents,
  getAvailableSchoolStudents,
  getSpecialEducationEmployees,
  resetAlternativeLocation,
} from '@/modules/alternative-location-management/api';
import { getSysparameterByNames } from '@/modules/system-parameter-managment/api';

const SPECIAL_POSTION_NAME = 'Puesto Vacante';

const STUDENT_FILTER_TYPES = {
  ASSIGNED: 'ASSIGNED',
  AVAILABLE: 'AVAILABLE',
};

const SAEE10_INDIVIDUAL_FLAG_ENABLED_FROM_ACADEMIC_YEAR_ID =
  'Saee10IndividualFlagEnabledFromAcademicYearId';

export default {
  name: 'Management',
  mixins: [alert],
  components: {
    PageHeader,
    ActionButton,
    DropDown,
    CustomSeparator,
    Pagination,
    CustomResultNotFound,
    SectionTitle,
    InformationHeader,
    GeneralInformation,
    StudentResultRow,
    StudentsHeaderFilter,
    AddStudent,
    EditStudentCapacity,
    AssistantsSection,
    AddTeacherSection,
  },
  data() {
    return {
      page: {
        icon: 'fas fa-user-edit',
        title: 'Manejo de Alternativa de Ubicación',
        routes: [
          'Inicio',
          'Recursos Humanos',
          'Manejo de Alternativa de Ubicación',
        ],
      },
      separatorOptions: [
        {
          id: STUDENT_FILTER_TYPES.ASSIGNED,
          name: 'Estudiantes Asignados',
          active: true,
        },
        {
          id: STUDENT_FILTER_TYPES.AVAILABLE,
          name: 'Estudiantes Disponibles',
        },
      ],
      currentTeacherId: 0,
      currentStudentFilterType: STUDENT_FILTER_TYPES.ASSIGNED,
      searchField: null,
      showAddStudent: false,
      showChangeStudentCapacity: false,
      changeTeacherActivated: false,
      pagination: {
        perPage: 10,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
      model: {
        teacher: null,
        email: null,
        phone: null,
        students: {},
        alternativeId: null,
      },
      collections: {
        resources: [],
        teachers: [],
      },
      allianceSchoolCodes: [],
      isBulkAssistantAssignmentAvailable: false,
      showAddTeacherSection: false,
    };
  },
  methods: {
    async getAllianceSchoolCodes() {
      const { data } = await getSysparameterByNames({
        parameterNames: ['AllianceSchoolsIds'],
      });

      if (data && data?.length) {
        const schoolCodes = data[0]?.staticValue?.split(',');
        this.allianceSchoolCodes = schoolCodes; // this are in STR ex. '07524'
      }
    },
    async setBulkAssistantAssignmentAvailability() {
      this.isBulkAssistantAssignmentAvailable = false;

      const academicYearId = parseInt(this.model?.academicYear?.id, 10);
      if (!academicYearId) return;

      try {
        const { data } = await getSysparameterByNames({
          parameterNames: [
            SAEE10_INDIVIDUAL_FLAG_ENABLED_FROM_ACADEMIC_YEAR_ID,
          ],
        });

        const enabledFromAcademicYearId = parseInt(data?.[0]?.staticValue, 10);
        this.isBulkAssistantAssignmentAvailable =
          !isNaN(enabledFromAcademicYearId) &&
          academicYearId >= enabledFromAcademicYearId;
      } catch (err) {
        this.isBulkAssistantAssignmentAvailable = false;
      }
    },
    async onSave() {
      const isValid = await this.$refs['general-information-form'].validate();
      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor de completar la información solicitada.',
          'error'
        );
        return isValid;
      }
      const teacher =
        this.model.teacher?.name === SPECIAL_POSTION_NAME
          ? this.collections.teachers.find(
              (t) => t.name === SPECIAL_POSTION_NAME
            ) ?? this.model.teacher
          : this.model.teacher;

      const payload = {
        id: this.model.id,
        schoolAlternativeLocationId: parseInt(this.$route.params.id),
        email: this.model.email,
        phone: this.model.phone,
        teacher,
        ...teacher.value,
      };
      try {
        const response = await saveAlternativeLocationTeacher(payload);
        this.changeTeacherActivated = false;
        this.model = response.data;
        this.fetchResources();
        this.ShowToast(
          'Confirmación',
          'La información ha sido guardada exitosamente.',
          'success'
        );
      } catch (err) {
        if (err && err.response && err.response.data) {
          this.ShowToast('Error', err.response.data.message, 'error');
        } else {
          this.ShowToast('Error', err.message, 'error');
        }
      }
    },
    async onAssignAssistantsToSAEE10() {
      try {
        const { data } = await assignAssistantsToSAEE10(this.model.id);
        const assistantCount =
          data?.assignedAssistantCount ?? data?.AssignedAssistantCount ?? 0;
        const saee10Count =
          data?.updatedSAEE10Count ?? data?.UpdatedSAEE10Count ?? 0;

        this.ShowToast(
          '',
          `Se asignaron ${assistantCount} asistentes a ${saee10Count} SAEE10`,
          'success'
        );
      } catch (err) {
        if (err && err.response && err.response.data) {
          this.ShowToast('Error', err.response.data.message, 'error');
        } else {
          this.ShowToast('Error', err.message, 'error');
        }
      }
    },
    async doSearch(page = 1) {
      const mappedFilter = {
        searchField: this.searchField,
        assignToId: this.currentTeacherId,
        pageIndex: page,
        pageSize: this.pagination.perPage,
        schoolAlternativeLocationId: parseInt(this.$route.params.id),
      };
      let requestData = getAlternativeLocationTeacherStudents;

      if (this.isAvailableStudentsTabActive) {
        requestData = getAvailableSchoolStudents;
        mappedFilter.assignToId = this.model.school.id;
      }

      await requestData(mappedFilter).then((res) => {
        this.model.students = res.data;
        this.setPagination();
      });
    },
    async onSync() {
      syncAssignedStudent(this.currentTeacherId)
        .then(async (res) => {
          if (res.status == 200 && res.data.length) {
            await this.doSearch();
            this.students.forEach((student) => {
              if (res.data.includes(student.studentSieId))
                student.recentlyAdded = true;
            });
          }
        })
        .catch((err) => {
          if (err && err.response && err.response.data) {
            this.ShowToast('Error', err.response.data.message, 'error');
          } else {
            this.ShowToast('Error', err.message, 'error');
          }
        });
    },
    async fetchSpecialEducationEmployees() {
      const { data: teacherList } = await getSpecialEducationEmployees({
        sieSchoolCode: this.model.school.value,
      });
      this.collections.teachers = teacherList;
    },
    async fetchResources() {
      this.collections.resources = await resourcesService.LoadValidationMessages(
        ['R01210005', 'R01210008', 'R01210009', 'R01210013']
      );

      const { data: model } = await getAlternativeLocationTeacher({
        schoolAlternativeLocationId: this.$route.params.id,
      });
      this.model = model;
      this.model.alternativeId = this.$route.params.id;
      await this.setBulkAssistantAssignmentAvailability();

      await this.fetchSpecialEducationEmployees();

      if (this.model.id) {
        this.setCurrentTeacherId();
        this.setPagination();
      }
    },
    onStudentFilterChanged(filterType) {
      if (this.currentStudentFilterType === filterType) return;
      this.currentStudentFilterType = filterType;
      this.searchField = null;
      this.showAddStudent = false;
      this.doSearch();
    },
    async onChangeToNewHandler() {
      const payload = {
        assignToId: this.selectedTeacherId,
        schoolAlternativeLocationId: this.alternativeLocation,
      };
      try {
        await resetAlternativeLocation(payload);
        await this.fetchResources();
      } catch (err) {
        this.ShowToast(
          'Alerta',
          err?.response?.data?.message || err?.message,
          'error'
        );
      }
    },
    onSearch(searchField) {
      this.searchField = searchField;
      this.doSearch();
    },
    setPagination() {
      if (!this.model.students) return;
      this.pagination = {
        ...this.pagination,
        totalItemCount: this.model.students.totalItemCount,
        pageCount: this.model.students.pageCount,
        currentPage: this.model.students.currentPage,
        count: this.model.students.count,
      };
    },
    setCurrentTeacherId() {
      this.currentTeacherId = this.model.teacher.id;
    },
    onAddRemoveHandler() {
      this.doSearch(this.model.students.currentPage);
    },
    onStudentRemoved() {
      this.onAddRemoveHandler();
      // so reload the assistants list on the current page.
      const assistantsSection = this.$refs['assistants-section'];
      if (!assistantsSection) return;
      assistantsSection.fetchSelectedAssistants(
        this.model.assistants?.currentPage || 1
      );
    },
    onChangeStudentCapacity(capacity) {
      this.model.studentCapacity = capacity;
      this.showChangeStudentCapacity = false;
    },
    async onTeacherAdded() {
      this.showAddTeacherSection = false;
      await this.fetchSpecialEducationEmployees();
    },
  },
  computed: {
    visibleActionItems() {
      return this.actionItems.filter((item) => item.visible !== false);
    },
    actionItems() {
      return [
        {
          name: 'Cambiar Maestro',
          icon: 'fas fa-user-edit text-success',
          visible: this.showChangeTeacherButton,
          click: () => {
            this.changeTeacherActivated = true;
          },
        },
        {
          name: 'Cambiar a Nuevo',
          icon: 'fas fa-undo text-success',
          visible: this.showChangeToNewButton,
          click: () => this.onChangeToNewHandler(),
        },
        {
          name: 'Editar cupo',
          icon: 'fas fa-edit text-success',
          visible: true,
          click: () => {
            this.showChangeStudentCapacity = true;
          },
        },
        {
          name: 'Asignar Asistentes a SAEE10',
          icon: 'fas fa-user-check text-success',
          visible: !!this.model.id && this.isBulkAssistantAssignmentAvailable,
          click: () => this.onAssignAssistantsToSAEE10(),
        },
      ];
    },
    isSchoolInAlliance() {
      // school code, not school id
      const schoolCode = this.model?.school?.value;
      return this.allianceSchoolCodes.includes(schoolCode);
    },
    students() {
      return (this.model.students && this.model.students.data) || [];
    },
    textCount() {
      return `Total de Estudiantes ${
        this.isAssignedStudentsTabActive ? 'Asignados:' : 'Disponibles:'
      }`;
    },
    showAddStudentButton() {
      return (
        this.currentStudentFilterType == STUDENT_FILTER_TYPES.ASSIGNED &&
        !this.showAddStudent
      );
    },
    showAddStudentSection() {
      return (
        this.currentStudentFilterType == STUDENT_FILTER_TYPES.ASSIGNED &&
        this.showAddStudent
      );
    },
    showChangeTeacherButton() {
      return !!this.model.id && !this.changeTeacherActivated;
    },
    showChangeToNewButton() {
      return !!this.model.id;
    },
    alternativeLocation() {
      return +this.$route.params.id;
    },
    isAssignedStudentsTabActive() {
      return this.currentStudentFilterType == STUDENT_FILTER_TYPES.ASSIGNED;
    },
    isAvailableStudentsTabActive() {
      return this.currentStudentFilterType == STUDENT_FILTER_TYPES.AVAILABLE;
    },
    selectedTeacherId() {
      return this.model.teacher?.id;
    },
    showSyncStudentButton() {
      return (
        this.selectedTeacherId != null &&
        this.collections.teachers.some(
          (t) => t.id == this.selectedTeacherId && t.value.canSync
        )
      );
    },
    getId() {
      return parseInt(this.$route.params.id);
    },
    hasAddTeacherManuallyPermission() {
      return this.userHasPermissions(
        'academic.hr.alternativelocation.addteachermanually'
      );
    },
  },
  mounted() {
    this.getAllianceSchoolCodes();
    if (!this.$route.params.id || isNaN(this.$route.params.id))
      this.$router.push(`/humanres/locationalternativemanagement`);
    else this.fetchResources();
  },
};
</script>

<style scoped>
.p-10px {
  padding: 10px;
}

.actions-dropdown {
  list-style: none;
}
</style>
